import React, { useRef, useState, useEffect, useCallback } from "react";
import { useImageCache } from "@/app/hooks/useImageCache";
import { useCarrosselFrames } from "@/app/hooks/useCarrosselFrames";
import {
	CarrosselSource,
	getCarrosselTotalFrames,
	isCarrosselFrames,
} from "@/app/utils/carrossel";
import { throttle } from "lodash";

interface CarRotationProps {
	carrossel: CarrosselSource;
	totalFrames?: number;
}

const CarRotation = ({ carrossel, totalFrames: spriteFrameCount = 36 }: CarRotationProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [currentFrame, setCurrentFrame] = useState(0);
	const [dragging, setDragging] = useState(false);
	const [startX, setStartX] = useState(0);
	const [touchStartX, setTouchStartX] = useState(0);
	const [sprite, setSprite] = useState<HTMLImageElement | null>(null);
	const frameRef = useRef<number>(0);
	const rafRef = useRef<number>();

	const isFrameMode = isCarrosselFrames(carrossel);
	const spriteUrl = isFrameMode ? "" : carrossel;
	const frameUrls = isFrameMode ? carrossel : [];
	const totalFrames = getCarrosselTotalFrames(carrossel, spriteFrameCount);

	const { image: spriteImage, isLoading: isSpriteLoading } =
		useImageCache(spriteUrl);
	const { frames, isLoading: isFramesLoading } = useCarrosselFrames(frameUrls);
	const isLoading = isFrameMode ? isFramesLoading : isSpriteLoading;

	useEffect(() => {
		if (!isFrameMode) {
			setSprite(null);
		}
	}, [spriteUrl, isFrameMode]);

	useEffect(() => {
		if (!isFrameMode && spriteImage) {
			setSprite(spriteImage);
		}
	}, [spriteImage, isFrameMode]);

	const drawSpriteFrame = useCallback(
		(frame: number, spriteImage: HTMLImageElement) => {
			const canvas = canvasRef.current;
			if (!canvas) return;
			const ctx = canvas.getContext("2d");
			if (!ctx) return;

			const dpr = window.devicePixelRatio || 1;
			const containerWidth = canvas.width / dpr;
			const containerHeight = canvas.height / dpr;
			ctx.clearRect(0, 0, containerWidth, containerHeight);

			const frameWidth = spriteImage.width / totalFrames;
			const frameHeight = spriteImage.height;
			const scale = Math.min(
				containerWidth / frameWidth,
				containerHeight / frameHeight
			);
			const scaledWidth = frameWidth * scale;
			const scaledHeight = frameHeight * scale;
			const x = (containerWidth - scaledWidth) / 2;
			const y = (containerHeight - scaledHeight) / 2;

			ctx.drawImage(
				spriteImage,
				frameWidth * frame,
				0,
				frameWidth,
				frameHeight,
				x,
				y,
				scaledWidth,
				scaledHeight
			);
		},
		[totalFrames]
	);

	const drawImageFrame = useCallback(
		(frame: number, frameImage: HTMLImageElement) => {
			const canvas = canvasRef.current;
			if (!canvas) return;
			const ctx = canvas.getContext("2d");
			if (!ctx) return;

			const dpr = window.devicePixelRatio || 1;
			const containerWidth = canvas.width / dpr;
			const containerHeight = canvas.height / dpr;
			ctx.clearRect(0, 0, containerWidth, containerHeight);

			const scale = Math.min(
				containerWidth / frameImage.width,
				containerHeight / frameImage.height
			);
			const scaledWidth = frameImage.width * scale;
			const scaledHeight = frameImage.height * scale;
			const x = (containerWidth - scaledWidth) / 2;
			const y = (containerHeight - scaledHeight) / 2;

			ctx.drawImage(
				frameImage,
				0,
				0,
				frameImage.width,
				frameImage.height,
				x,
				y,
				scaledWidth,
				scaledHeight
			);
		},
		[]
	);

	const drawFrame = useCallback(
		(frame: number) => {
			if (isFrameMode) {
				const frameImage = frames[frame];
				if (frameImage) drawImageFrame(frame, frameImage);
				return;
			}

			if (sprite) drawSpriteFrame(frame, sprite);
		},
		[isFrameMode, frames, sprite, drawImageFrame, drawSpriteFrame]
	);

	useEffect(() => {
		drawFrame(currentFrame);
	}, [currentFrame, drawFrame]);

	useEffect(() => {
		const container = containerRef.current;
		const canvas = canvasRef.current;
		if (!container || !canvas) return;

		const updateCanvasSize = () => {
			const dpr = window.devicePixelRatio || 1;
			const containerWidth = container.clientWidth;
			if (!containerWidth) return;

			const aspectRatio = 16 / 9;
			const containerHeight = containerWidth / aspectRatio;

			canvas.style.width = `${containerWidth}px`;
			canvas.style.height = `${containerHeight}px`;
			canvas.width = containerWidth * dpr;
			canvas.height = containerHeight * dpr;

			const ctx = canvas.getContext("2d");
			if (ctx) {
				ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
				ctx.imageSmoothingEnabled = true;
				ctx.imageSmoothingQuality = "high";
			}
			drawFrame(currentFrame);
		};

		updateCanvasSize();

		const resizeObserver = new ResizeObserver(() => {
			updateCanvasSize();
		});
		resizeObserver.observe(container);
		window.addEventListener("resize", updateCanvasSize);

		let attempts = 0;
		let raf = 0;
		const retryUntilVisible = () => {
			if (container.clientWidth === 0 && attempts < 30) {
				attempts += 1;
				raf = requestAnimationFrame(retryUntilVisible);
				return;
			}
			updateCanvasSize();
		};
		raf = requestAnimationFrame(retryUntilVisible);

		return () => {
			cancelAnimationFrame(raf);
			resizeObserver.disconnect();
			window.removeEventListener("resize", updateCanvasSize);
		};
	}, [currentFrame, drawFrame]);

	const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
		setDragging(true);
		setStartX(e.clientX);
	};

	const handleMouseMove = useCallback(
		throttle((e: React.MouseEvent<HTMLCanvasElement>) => {
			if (!dragging) return;
			const deltaX = e.clientX - startX;
			const sensitivity = 70;
			const framesToMove = Math.round(deltaX / sensitivity);
			frameRef.current =
				(((currentFrame + framesToMove) % totalFrames) + totalFrames) %
				totalFrames;

			if (rafRef.current) cancelAnimationFrame(rafRef.current);
			rafRef.current = requestAnimationFrame(() => {
				setCurrentFrame(frameRef.current);
			});
		}, 16),
		[dragging, startX, currentFrame, totalFrames]
	);

	const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
		setDragging(true);
		setTouchStartX(e.touches[0].clientX);
	};

	const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
		if (!dragging) return;
		const deltaX = e.touches[0].clientX - touchStartX;
		const sensitivity = 5;
		const framesToMove = Math.round(deltaX / sensitivity);
		const newFrame =
			(((currentFrame + framesToMove) % totalFrames) + totalFrames) %
			totalFrames;
		setCurrentFrame(newFrame);
		setTouchStartX(e.touches[0].clientX);
	};

	return (
		<div ref={containerRef} className="w-full relative">
			{isLoading && (
				<div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50">
					<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600" />
				</div>
			)}
			<canvas
				className="cursor-grab select-none"
				ref={canvasRef}
				onMouseDown={handleMouseDown}
				onMouseMove={handleMouseMove}
				onMouseUp={() => setDragging(false)}
				onMouseLeave={() => setDragging(false)}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={() => setDragging(false)}
				style={{ width: "100%", height: "auto", touchAction: "none" }}
			/>
			<div className="flex justify-center items-center mt-4 text-sm text-gray-600">
				<p>Segure e Arraste para girar</p>
			</div>
		</div>
	);
};

export default CarRotation;
